import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Breadcrumb, Loader, Nav, Panel } from 'rsuite';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { toastErrorMessage } from '@/utils/toasterMessage';
import { Story } from '@/models/story.model';
import { getStory } from '@/services/story.service';
import StoryForm from './story-form';
import StoryChapterListPage from '../story-chapter/list/story-chapter-list';
import EncyclopediaRelationList from '@/pages/encyclopedia/relation/encyclopedia-relation-list';
import PageHeader from '@/components/PageHeader';

const CustomNav = ({ active, onSelect, newMode, ...props }) => {
  return (
    <Nav {...props} activeKey={active} onSelect={onSelect}>
      <Nav.Item eventKey="story-form">Detail</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="story-chapters">Chapters</Nav.Item>
      <Nav.Item disabled={newMode} eventKey="story-encyclopedia-items">Encyclopedia Items</Nav.Item>
    </Nav>
  );
};

const Page = () => {

  const requestedTab = useLocation()?.state?.tab;

  const [activeTab, setActiveTab] = useState('story-form');
  const [formObject, setFormObject] = useState<Story|undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();

  const { id: idParam } = useParams();
  const id = idParam ? parseInt(idParam, 10) : undefined;
  const pageState = id ? PageState.Edit : PageState.New;


  useEffect(() => {
    if (pageState == PageState.Edit) {
      fetchData();
    } else {
      setFormObject(undefined);
    }
    setActiveTab(pre => requestedTab ?? pre);
  }, [id]);
  
  const fetchData = async () => {
    if (id) {
      setLoading(true);
      try {
        const result: BaseResponseModel<Story> = await getStory(id);
        if (result.success) {
          setFormObject(result.result);
        }
      } catch (err) {
        const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
        toastErrorMessage(`Error in fetching Story: ${errorModel?.errorMessage}`);
        nav('stories');
      }
      finally {
        setLoading(false);
      }
    }
  };
 
  return (
    <Panel
      header={
        <PageHeader title={pageState == PageState.Edit && formObject ? `Edit Story: ${formObject.name}` : 'New Story'}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item href='stories'> Stories</Breadcrumb.Item>
            <Breadcrumb.Item active>{pageState == PageState.Edit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </PageHeader>
      }
    >
      <CustomNav appearance="subtle" active={activeTab} onSelect={key => setActiveTab(key)} newMode={pageState == PageState.New} />
      {
        loading ? <Loader center content="loading ..." /> :
        <>
          {activeTab == 'story-form' && <StoryForm formObject={formObject} pageState={pageState}/>}
          {activeTab == 'story-chapters' && id && <StoryChapterListPage storyId={formObject?.id}/>}
          {activeTab == 'story-encyclopedia-items' && id && <EncyclopediaRelationList storyId={formObject?.id}/>}
        </>
      }
    </Panel>
  );
};

export default Page;
